/**
 * onFieldRoster — who is CURRENTLY on the pitch for each side, applying
 * substitution events on top of the matchday sheet's `is_starter` flags.
 *
 * Lifted out of `PublicMatchAnalytics.tsx` (previously a component-local
 * `useMemo`, ~lines 628-652) so it has exactly one implementation instead of
 * two: the public page's `LiveMatchContextBanner` needs "who's on the pitch
 * right now". `substituteFor` (which outgoing player each substitute
 * replaced) was originally added for PMA-COCK-134's vertical lineup pitch
 * (`components/matchPitch/`, removed 2026-08-24 — see cockpit-contract.md);
 * kept here as a generically useful part of this module's return shape even
 * though nothing currently consumes it.
 */
import type { Match, Player } from "../pages/logger/types";
import type { MatchEvent } from "../store/useMatchLogStore";

export interface OnFieldRoster {
  home: Player[];
  away: Player[];
  /** Incoming player id -> outgoing player id they replaced. Home and away
   *  are merged into one map since player ids are unique across both sides;
   *  only set when a Substitution event carries BOTH `player_off_id` and
   *  `player_on_id` — an incomplete event still adds the incoming player
   *  (matching the original behaviour) but can't record who they replaced. */
  substituteFor: Map<string, string>;
}

/**
 * `match` only needs the two team rosters + ids — accepts the full `Match`
 * for convenience at the call site.
 */
export function computeOnFieldRoster(
  match: Pick<Match, "home_team" | "away_team">,
  events: MatchEvent[],
): OnFieldRoster {
  const homeIds = new Set(
    match.home_team.players.filter((p) => p.is_starter).map((p) => p.id),
  );
  const awayIds = new Set(
    match.away_team.players.filter((p) => p.is_starter).map((p) => p.id),
  );
  const substituteFor = new Map<string, string>();

  for (const evt of events) {
    if (evt.type !== "Substitution" || !evt.data) continue;
    const playerOff = evt.data.player_off_id as string | undefined;
    const playerOn = evt.data.player_on_id as string | undefined;
    const ids = evt.team_id === match.home_team.id ? homeIds : awayIds;
    if (playerOff) ids.delete(playerOff);
    if (playerOn) {
      ids.add(playerOn);
      if (playerOff) substituteFor.set(playerOn, playerOff);
    }
  }

  return {
    home: match.home_team.players.filter((p) => homeIds.has(p.id)),
    away: match.away_team.players.filter((p) => awayIds.has(p.id)),
    substituteFor,
  };
}
